import React from 'react';
import { EnvoiAutoCard } from '../Planning/EnvoiAutoCard';

/**
 * Un seul endroit pour tous les documents envoyés automatiquement par e-mail. Chaque envoi n'apparaît que si le module
 * correspondant est activé pour l'établissement. Tout est désactivé tant que le titulaire ne l'active pas.
 * Ajouter un document = une ligne dans ENVOIS (le serveur, lui, a son propre registre : functions/api/envois-auto.js).
 */
const ENVOIS = [
    {
        type: 'heures', module: 'planning', titre: 'Récapitulatif des heures',
        interrupteur: 'Envoyer le récapitulatif des heures par e-mail',
        description: 'Un fichier Excel et un fichier CSV des heures de chaque salarié sont envoyés automatiquement, par exemple à votre comptable.',
        avertissement: "Ce document contient les heures de votre équipe : n'indiquez que des adresses de confiance.",
    },
    {
        type: 'factures', module: 'facture', titre: 'Factures et encaissements',
        interrupteur: 'Envoyer les factures et les encaissements par e-mail',
        description: 'Un fichier Excel et un fichier CSV des factures émises, de ce qui est encaissé et de ce qui reste dû, par exemple à votre comptable.',
        avertissement: "Ce document contient les noms de vos clients et vos montants : n'indiquez que des adresses de confiance.",
    },
    {
        type: 'absences', module: 'demande_conges', titre: 'Absences et congés',
        interrupteur: 'Envoyer les absences et congés par e-mail',
        description: 'Un fichier Excel et un fichier CSV des absences et congés du mois écoulé (ou de la semaine écoulée).',
        avertissement: "Ce document contient les noms de vos salariés et leurs absences : n'indiquez que des adresses de confiance.",
    },
    {
        type: 'reservations', module: 'livre_reservations', avenir: true, titre: 'Réservations à venir',
        interrupteur: 'Envoyer les réservations à venir par e-mail',
        description: 'Un fichier Excel et un fichier CSV des réservations des 7 prochains jours (ou des 30 prochains jours en rythme mensuel).',
        avertissement: "Ce document contient les noms et téléphones de vos clients : n'indiquez que des adresses de confiance.",
    },
    {
        type: 'evenements', module: 'evenementiel', avenir: true, titre: 'Événements et privatisations à venir',
        interrupteur: 'Envoyer les événements et privatisations à venir par e-mail',
        description: 'Un fichier Excel et un fichier CSV des événements et privatisations des 7 prochains jours (ou des 30 prochains jours en rythme mensuel).',
        avertissement: "Ce document contient les coordonnées de vos clients : n'indiquez que des adresses de confiance.",
    },
    {
        type: 'bons_commande', module: 'fournisseurs', ouModule: 'commandes', sansCalendrier: true, titre: 'Copie des bons de commande',
        interrupteur: 'Recevoir une copie de chaque commande envoyée à un fournisseur',
        description: 'À chaque commande envoyée à un fournisseur, une copie du message part aussi vers les adresses ci-dessous.',
        avertissement: "Les commandes ne partent jamais ailleurs que chez le fournisseur et chez vous : n'indiquez que des adresses de confiance.",
    },
] as const;

const disponible = (e: (typeof ENVOIS)[number], hasModule: (nom: string) => boolean) =>
    hasModule(e.module) || ('ouModule' in e && hasModule(e.ouModule));

export function EnvoisAutoSettings({ hasModule }: { hasModule: (nom: string) => boolean }) {
    const visibles = ENVOIS.filter((e) => disponible(e, hasModule));
    return (
        <div className="flex flex-col gap-8 max-w-[560px]">
            <p className="text-[12.5px] leading-snug text-content-muted text-pretty">
                Recevez vos documents par e-mail, automatiquement, ou faites-les envoyer à votre comptable. Rien n'est envoyé tant que vous
                n'avez pas activé un envoi et enregistré au moins une adresse.
            </p>
            {visibles.length === 0 && <p className="text-[12.5px] text-content-muted">Aucun envoi automatique n'est disponible avec vos modules actuels.</p>}
            {visibles.map((e) => (
                <section key={e.type} aria-labelledby={`envois-${e.type}`} className="flex flex-col gap-3 border-t border-border pt-6 first:border-t-0 first:pt-0">
                    <h3 id={`envois-${e.type}`} className="text-[13px] font-semibold text-content">{e.titre}</h3>
                    <EnvoiAutoCard type={e.type} titre={e.interrupteur} descriptionActive={e.description} avertissement={e.avertissement} avenir={'avenir' in e} sansCalendrier={'sansCalendrier' in e} />
                </section>
            ))}
        </div>
    );
}
